import React, { useState, useCallback, useEffect } from "react";
import api from "../../common/lib/api";
import naturalCompare from "natural-compare";
import { CompanyLinkChangeButton } from "../CompanyLinkChangeButton";
import { Checkbox, Button } from "@blueprintjs/core";
import updateLinkFor from "./updateLinkFor";
import { shortenAtlasId } from "../shortenAtlasId";
import StageHeader from "./StageHeader";
import Managed, { useManaged } from "../../common/components/Managed";

const DEFAULT_DATA = {
  companies: [],
  accountingCompanies: {},
};

export default function ReviewLinks({
  system,
  period,
  stage,
  stageIndex,
  commands,
  onRefresh,
}) {
  const [state, setData, setError] = useManaged(DEFAULT_DATA);
  const [problemsOnly, setProblemOnly] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/bill/${system}/${period}/${stage.stage}/companies`);
      const acCompanies = {};
      for (const comp of data.accountingCompanies) {
        acCompanies[comp.companyId] = comp;
      }
      const companies = data.companies.sort((a, b) => {
        const aa = a.accountingName;
        const bb = b.accountingName;

        if (!aa && bb) {
          return 1;
        }
        if (aa && !bb) {
          return -1;
        }

        if (aa && bb && aa !== bb) {
          return naturalCompare(aa, bb);
        }

        if (a.name && b.name) {
          return naturalCompare(a.name.toLowerCase(), b.name.toLowerCase());
        }
        return naturalCompare(a.companyId, b.companyId);
      });

      setData({
        accountingCompanies: acCompanies,
        companies,
      });
    } catch (e) {
      setError(e);
    }
  }, [period, setData, setError, stage.stage, system]);

  const updateLink = updateLinkFor(load, setError);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = state.data.companies.filter(
    (x) => !problemsOnly || x.status !== "active"
  );
  return (
    <>
      <StageHeader
        stageIndex={stageIndex}
        stage={stage}
      />
      <Managed state={state}>
        <>
          <Checkbox
            checked={problemsOnly}
            label="Don't show all items"
            onChange={(e) => setProblemOnly(e.target.checked)}
          />
          {state.status !== "init" && !filtered.length && (
            <p>No issues found</p>
          )}
          {state.status !== "init" && (
            <Button
              small
              onClick={() => {
                setData(DEFAULT_DATA);
                load();
              }}
            >
              Refresh
            </Button>
          )}
          {filtered.length > 0 && (
            <table className="bp3-html-table bp3-html-table-bordered bp3-html-table-condensed bp3-html-table-striped">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Atlas Name</th>
                  <th>MYOB Name</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((company) => {
                  return (
                    <tr key={company.companyId}>
                      <td>{shortenAtlasId(company.companyId)}</td>
                      <td title={company.companyId}>
                        {company.name}
                        {company.status !== "active" && (
                          <>
                            {" "}
                            <span className="bp3-tag">{company.status}</span>
                          </>
                        )}
                      </td>
                      <td>{company.accountingName}</td>
                      <td>
                        <CompanyLinkChangeButton
                          company={company}
                          accountingCompanies={state.data.accountingCompanies}
                          updateLink={updateLink}
                          system={system}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      </Managed>
    </>
  );
}
